import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { logoUrlOf, sharePayload, shareApi, uploadLogo } from '@/lib/bracket/shareApi';

const DEBOUNCE_MS = 900;

// Keeps a bracket's share link in step with the editor: the server copy wins
// when the editor opens, then every edit is pushed after a short pause.
export function useShareSync({ templateId, c, setC, settings, initialShareId }) {
  const [share, setShare] = useState(null);
  const [status, setStatus] = useState('idle');
  const sent = useRef(null);
  const chain = useRef(Promise.resolve());
  const uploading = useRef('');

  const forget = useCallback(() => {
    setShare(null);
    sent.current = null;
    setStatus('idle');
    setC((prev) => ({ ...prev, shareId: '' }));
  }, [setC]);

  useEffect(() => {
    const id = initialShareId || c.shareId;
    if (!id) return;
    shareApi.get(id).then((s) => {
      if (!s.is_owner) { forget(); return; }
      const next = { names: s.names, scores: s.scores || {}, title: s.title, date: s.date, logo: logoUrlOf(s.logo_path) };
      sent.current = JSON.stringify(sharePayload(templateId, next, s.settings));
      setShare(s);
      setC((prev) => ({ ...prev, ...next, active: true, shareId: id, eventId: s.event?.id || prev.eventId || '' }));
    }).catch((e) => { if (e.status === 404) forget(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!c.shareId || !share || !settings) return undefined;
    const t = setTimeout(() => {
      if (c.logo.startsWith('data:')) {
        if (uploading.current === c.logo) return;
        const data = c.logo;
        uploading.current = data;
        setStatus('saving');
        uploadLogo(data)
          .then((url) => setC((prev) => (prev.logo === data ? { ...prev, logo: url } : prev)))
          .catch((e) => { setStatus('error'); toast.error('Logo could not be uploaded', { description: e.message }); })
          .finally(() => { uploading.current = ''; });
        return;
      }
      const body = sharePayload(templateId, c, settings);
      const json = JSON.stringify(body);
      if (json === sent.current) return;
      sent.current = json;
      setStatus('saving');
      chain.current = chain.current
        .then(() => shareApi.update(c.shareId, body))
        .then((s) => { setShare(s); setStatus('saved'); })
        .catch((e) => {
          sent.current = null;
          if (e.status === 404 || e.status === 403) { forget(); toast.error('This share link is no longer yours or was removed'); } else setStatus('error');
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [c, settings, share?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = useCallback(async () => {
    setStatus('saving');
    try {
      const logo = c.logo.startsWith('data:') ? await uploadLogo(c.logo) : c.logo;
      const body = sharePayload(templateId, { ...c, logo }, settings);
      const s = await shareApi.create(body);
      sent.current = JSON.stringify(body);
      setShare(s);
      setC((prev) => ({ ...prev, logo, shareId: s.id }));
      setStatus('saved');
    } catch (e) {
      setStatus('error');
      toast.error('Could not create the share link', { description: e.message });
    }
  }, [c, settings, templateId, setC]);

  const stop = useCallback(async () => {
    try {
      await shareApi.remove(c.shareId);
      forget();
      toast.success('Sharing stopped', { description: 'The old link no longer opens this bracket.' });
    } catch (e) { toast.error('Could not stop sharing', { description: e.message }); }
  }, [c.shareId, forget]);

  return { share: c.shareId ? share : null, status, create, stop };
}
